import axios from "axios";
import { apiURI } from "../ENVs";

/**
 * Streams a server-generated CSV export to the browser as a file download.
 *
 * The backend builds the CSV from the FULL result set matching the active
 * search + sort (see BackEnd exportCsv.streamCsv), reusing the SAME list
 * endpoint with `format=csv` — so no separate route, and the JWT /
 * subscription-key headers added by the axios interceptor are sent (a native
 * browser download couldn't send them and would be rejected by the gateway).
 *
 * The request goes through axios as a blob, so it survives in-app navigation
 * (it isn't tied to a page component) and never blocks the UI. The response is
 * gzipped on the wire by the backend compression middleware and transparently
 * decompressed by the browser.
 *
 * @param {Object}  args
 * @param {string}  args.endpoint  list endpoint after `${apiURI}/form/`
 *   (e.g. "getItemsAll", "requests", `execChefApproverList/${email}`).
 * @param {Object}  [args.params]  extra query params (search / sort JSON), same
 *   shape the paginated fetch builds — `format=csv` is added here.
 * @param {string}  args.filename  name the saved file gets (a blob: URL has no
 *   filename, so it must be set explicitly on the anchor).
 * @throws re-throws the axios error (with a decoded `.message` when the backend
 *   returned a JSON error blob) so the caller can surface it.
 */
export async function downloadCsv({ endpoint, params = {}, filename }) {
  try {
    const { data } = await axios.get(`${apiURI}/form/${endpoint}`, {
      params: { ...params, format: "csv" },
      responseType: "blob",
    });

    const objectUrl = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after a tick so the browser has started reading the blob for the
    // download (revoking synchronously can cancel the download in some browsers).
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch (error) {
    // An error response also arrives as a blob (responseType:"blob"); decode it
    // to a readable message so callers don't surface "[object Blob]".
    if (error?.response?.data instanceof Blob) {
      try {
        const asText = await error.response.data.text();
        const parsed = JSON.parse(asText);
        error.message = parsed?.error || asText || error.message;
      } catch {
        // Leave the original error message if the blob isn't JSON.
      }
    }
    throw error;
  }
}
